class ProductOptionsWidget extends HTMLElement {
  constructor() {
    super();
    this.activeVariant = null;
    this.activeSellingPlanId = window.selectedSellingPlanId;
    this.activeSellingPlanName = window.selectedSellingPlanName || '';
    this.purchaseType = 'onetime';
    this.cacheDOM();
    this.init();
  }

  cacheDOM() {
    this.dom = {
      onetimePrice: document.querySelector('[data-price="onetime"]'),
      onetimeComparePrice: document.querySelector(
        '[data-price="onetime-compare"]'
      ),
      subscriptionPrice: document.querySelector('[data-price="subscription"]'),
      subscriptionComparePrice: document.querySelector(
        '[data-price="subscription-compare"]'
      ),
      toggleButtons: document.querySelectorAll(
        '.purchase-options-widget__toggle-button'
      ),
      subscriptionOptions: document.querySelectorAll(
        '.purchase-options-widget__option'
      ),
      sellingPlans: document.querySelectorAll(
        '.purchase-options-widget__subscription-option'
      ),
      subscriptionOptionsContainer: document.querySelector(
        '.purchase-options-widget__subscription-options'
      ),
      deliveryDate: document.querySelector(
        '.purchase-options-widget__subscription-option-delivery-date'
      ),
      sellingPlanInput: document.getElementById('product-selling_plan'),
      atcButtonPrice: document.getElementById('atc_button_price'),
      peopleCount: document.querySelectorAll(
        '.purchase-options-widget__subscription-option-people-count'
      ),
      subscriptionSelectionGuideContentSetOf2: document.querySelector(
        '.bx-subscription-selection-guide-content-set-of-2'
      ),
      subscriptionSelectionGuideContentSetOf4: document.querySelector(
        '.bx-subscription-selection-guide-content-set-of-4'
      ),
      truemedContainer: document.querySelector('.truemed-widget-wrapper'),
    };
  }

  init() {
    const {
      sellingPlans,
      deliveryDate,
      toggleButtons,
      subscriptionOptions,
      subscriptionOptionsContainer,
      sellingPlanInput,
    } = this.dom;

    this.activeVariant = window.variants.find(
      v => v.id === window.selectedVariantId
    );
    if (sellingPlans.length > 0) {
      deliveryDate.innerHTML = this.getNextDeliveryDateFromText(
        sellingPlans[0].dataset.sellingPlanName
      );
    }

    // Ensure onetime is selected by default
    this.purchaseType = 'onetime';
    this.toggleActiveOption(subscriptionOptions, 'onetime');
    if (subscriptionOptionsContainer) {
      subscriptionOptionsContainer.classList.add('hidden');
    }
    if (sellingPlanInput) {
      sellingPlanInput.value = '';
    }
    sellingPlans.forEach(plan => plan.classList.remove('active'));

    this.setupToggleButtons(toggleButtons);
    this.setupSellingPlans(sellingPlans);
    this.listenVariantChange();

    // Emit initial state to update prices on load
    this.emitInitialState();
  }

  emitInitialState() {
    // Get initial plan based on purchase type
    let plan = null;
    if (this.purchaseType === 'subscription') {
      plan = this.activeVariant?.selling_plan_allocations?.find(
        alloc => alloc.selling_plan_id === Number(this.activeSellingPlanId)
      );
    }

    this.emitSubscriptionUpdate(plan);
  }

  setupToggleButtons(toggleButtons) {
    toggleButtons.forEach(button =>
      button.addEventListener('click', () =>
        this.handlePurchaseTypeToggle(button)
      )
    );
  }

  setupSellingPlans(sellingPlans) {
    sellingPlans.forEach(planElement =>
      planElement.addEventListener('click', () =>
        this.handleSellingPlanSelect(planElement)
      )
    );
  }

  handlePurchaseTypeToggle(button) {
    const {
      subscriptionOptions,
      subscriptionOptionsContainer,
      sellingPlanInput,
      sellingPlans,
      truemedContainer,
    } = this.dom;
    this.purchaseType = button.dataset.option;

    const plan = this.activeVariant?.selling_plan_allocations?.find(
      alloc => alloc.selling_plan_id === Number(this.activeSellingPlanId)
    );

    this.toggleActiveOption(subscriptionOptions, this.purchaseType);

    if (this.purchaseType === 'onetime') {
      sellingPlanInput.value = '';
      sellingPlans.forEach(plan => plan.classList.remove('active'));
      subscriptionOptionsContainer?.classList.add('hidden');
      truemedContainer?.classList.remove('is-hidden');
      this.emitSubscriptionUpdate(plan);
      return;
    }

    // Subscription selected
    subscriptionOptionsContainer?.classList.remove('hidden');
    truemedContainer?.classList.add('is-hidden');

    // If no plan is selected, automatically select the first one
    if (!this.activeSellingPlanId && sellingPlans.length > 0) {
      const firstPlan = sellingPlans[0];
      this.activeSellingPlanId = firstPlan.dataset.sellingPlanId;
      this.activeSellingPlanName = firstPlan.dataset.sellingPlanName;
      firstPlan.classList.add('active');

      const { deliveryDate } = this.dom;
      if (deliveryDate) {
        deliveryDate.innerHTML = this.getNextDeliveryDateFromText(
          firstPlan.dataset.sellingPlanName
        );
      }
    } else {
      // Use existing selected plan
      const activePlanEl = document.querySelector(
        `.purchase-options-widget__subscription-option[data-selling-plan-id="${this.activeSellingPlanId}"]`
      );
      activePlanEl?.classList.add('active');
    }

    sellingPlanInput.value = this.activeSellingPlanId;

    // Get the plan allocation for the selected plan
    const selectedPlan = this.activeVariant?.selling_plan_allocations?.find(
      alloc => alloc.selling_plan_id === Number(this.activeSellingPlanId)
    );

    this.emitSubscriptionUpdate(selectedPlan);
  }

  handleSellingPlanSelect(planElement) {
    const {
      sellingPlans,
      deliveryDate,
      sellingPlanInput,
      subscriptionPrice,
      atcButtonPrice,
    } = this.dom;

    sellingPlans.forEach(plan => plan.classList.remove('active'));
    planElement.classList.add('active');

    this.activeSellingPlanId = planElement.dataset.sellingPlanId;
    this.activeSellingPlanName = planElement.dataset.sellingPlanName;
    sellingPlanInput.value = this.activeSellingPlanId;
    deliveryDate.innerHTML = this.getNextDeliveryDateFromText(
      planElement.dataset.sellingPlanName
    );

    const plan = this.activeVariant?.selling_plan_allocations?.find(
      alloc => alloc.selling_plan_id === Number(this.activeSellingPlanId)
    );

    if (plan && subscriptionPrice) {
      subscriptionPrice.innerHTML = this.convertPrice(plan.price);
    }

    // ✅ Emit update event when plan changes
    this.emitSubscriptionUpdate(plan);
  }

  listenVariantChange() {
    document.addEventListener('variant:change', e => {
      const variantId = e.detail.variantId;
      this.activeVariant = window.variants.find(v => v.id === variantId);
      this.updatePrices();

      // ✅ Also emit subscription update when variant changes
      const plan = this.activeVariant?.selling_plan_allocations?.find(
        alloc => alloc.selling_plan_id === Number(this.activeSellingPlanId)
      );

      if (this.activeVariant.title == 'Set of 4 Filters') {
        this.dom.subscriptionSelectionGuideContentSetOf4.style.display =
          'block';
        this.dom.subscriptionSelectionGuideContentSetOf2.style.display = 'none';
        this.dom.peopleCount.forEach(count => {
          if (count.dataset.planName == '9 month') {
            count.innerHTML = '(4-6 people)';
          } else if (count.dataset.planName == '12 month') {
            count.innerHTML = '(2-4 people)';
          }
        });
      } else {
        this.dom.subscriptionSelectionGuideContentSetOf4.style.display = 'none';
        this.dom.subscriptionSelectionGuideContentSetOf2.style.display =
          'block';
        this.dom.peopleCount.forEach(count => {
          if (count.dataset.planName == '9 month') {
            count.innerHTML = '(3-4 people)';
          } else if (count.dataset.planName == '12 month') {
            count.innerHTML = '(1-2 people)';
          }
        });
      }

      this.emitSubscriptionUpdate(plan);
    });
  }

  updatePrices() {
    const {
      onetimePrice,
      onetimeComparePrice,
      subscriptionPrice,
      subscriptionComparePrice,
    } = this.dom;
    const variant = this.activeVariant;
    if (!variant) return;

    const plan =
      variant.selling_plan_allocations?.find(
        alloc => alloc.selling_plan_id === Number(this.activeSellingPlanId)
      ) || variant.selling_plan_allocations?.[0];

    if (onetimePrice) onetimePrice.innerHTML = this.convertPrice(variant.price);
    if (onetimeComparePrice)
      onetimeComparePrice.innerHTML = this.convertPrice(
        variant.compare_at_price
      );
    if (subscriptionPrice && plan)
      subscriptionPrice.innerHTML = this.convertPrice(plan.price);
    if (subscriptionComparePrice)
      subscriptionComparePrice.innerHTML = this.convertPrice(variant.price);
  }

  getNextDeliveryDateFromText(text, startDate = new Date()) {
    const monthsMatch = text?.match(/(\d+)\s*month/i);
    if (!monthsMatch) return '';

    const monthsToAdd = parseInt(monthsMatch[1]);
    const nextDate = new Date(startDate);
    nextDate.setMonth(nextDate.getMonth() + monthsToAdd);

    return nextDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  convertPrice(price) {
    return `${window.shopCurrency.symbol}${(price / 100).toFixed(2)}`;
  }

  toggleActiveOption(options, activeOption) {
    options.forEach(option => {
      option.classList.toggle('active', option.dataset.option === activeOption);
    });
  }

  emitSubscriptionUpdate(plan) {
    this.dispatchEvent(
      new CustomEvent('subscription:update', {
        bubbles: true,
        detail: {
          variant: this.activeVariant,
          purchaseType: this.purchaseType,
          plan: plan,
          sellingPlanName: this.activeSellingPlanName,
        },
      })
    );
  }
}

customElements.define('product-options-widget', ProductOptionsWidget);

// Bx Subscription Selection Guide Modal
function initBxSubscriptionSelectionGuideModal() {
  const modal = new BorouxModal({
    id: 'bx-subscription-selection-guide-modal',
    onOpen: () => console.log('Bx subscription guide modal opened'),
    onClose: () => console.log('Bx subscription guide modal closed'),
  });

  document
    .querySelectorAll('[data-open-bx-subscription-selection-guide-modal]')
    .forEach(button => {
      button.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        modal.open();
      });
    });

  window.bxSubscriptionSelectionGuideModal = modal;
}

// Bx Subscription Selection Guide Modal
function initBxHowManyFiltersModal() {
  const modal = new BorouxModal({
    id: 'bx-how-many-filters-modal',
    onOpen: () => console.log('Bx how many filters modal opened'),
    onClose: () => console.log('Bx how many filters modal closed'),
  });

  document
    .querySelectorAll('[data-open-bx-how-many-filters-modal]')
    .forEach(button => {
      button.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        modal.open();
      });
    });

  window.bxHowManyFiltersModal = modal;
}

document.addEventListener(
  'DOMContentLoaded',
  initBxSubscriptionSelectionGuideModal
);
document.addEventListener('DOMContentLoaded', initBxHowManyFiltersModal);
